import * as React from "react";
import { UitkTertiaryButton } from "uitk-react-button";
import { UitkStepInput } from "uitk-react-step-input";
import { liveAnnounce, Level } from "uitk-react-utils";
import { AgeSelect } from "./AgeSelect";
import { locKeys } from "../l10n";
import { RoomProps } from "../typings";
import { useLocalization } from "@shared-ui/localization-context";
import { getTracker } from "utils/TrackingUtils";
import { inject } from "mobx-react";

export const Room = inject("analytics")((props: RoomProps) => {
  const roomRef: React.RefObject<HTMLHeadingElement> = React.createRef();
  props.roomRefs.push(roomRef);

  const { formatText } = useLocalization();

  const onUpdateRoomAdults = (roomIndex: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, index) => {
        if (index === roomIndex) {
          return {
            ...room,
            adults: parseInt(event.target.value, 10),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });

    tracker.trackEvent(`${_guests}${roomIndex}.adults`);
  };

  const onUpdateRoomChildren = (roomIndex: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChildrenAdded = parseInt(event.target.value, 10) > props.travelers.rooms[roomIndex].children.length;
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, index) => {
        if (index === roomIndex) {
          return {
            ...room,
            children: isChildrenAdded
              ? room.children.concat({ age: props.config.minChildAge })
              : room.children.slice(0, -1),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });

    tracker.trackEvent(`${_guests}${roomIndex}.children`);
  };

  const onUpdateRoomInfants = (roomIndex: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isInfantAdded = parseInt(event.target.value, 10) > props.travelers.rooms[roomIndex].infants.length;
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, index) => {
        if (index === roomIndex) {
          return {
            ...room,
            infants: isInfantAdded
              ? room.infants.concat({ age: props.config.minInfantAge })
              : room.infants.slice(0, -1),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });

    tracker.trackEvent(`${_guests}${roomIndex}.infants`);
  };

  const onRemoveRoom = (roomIndex: number) => () => {
    props.setTravelers({
      rooms: props.travelers.rooms.filter((room, index) => roomIndex !== index),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.roomRemoved, ""), Level.POLITE);
    props.setTotalRoomRefs(props.totalRoomRefs - 1);

    tracker.trackEvent(`${_guests}remove-room`);
  };

  const onUpdateChildAge = (roomIndex: number, childIndex: number) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    const age = parseInt(event.target.value, 10);
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, i) => {
        if (i === roomIndex) {
          return {
            ...room,
            children: room.children.map((child, j) => {
              if (j === childIndex) {
                return { age };
              }

              return child;
            }),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.childUpdated, childIndex + 1, age), Level.POLITE);
  };

  const onUpdateInfantAge = (roomIndex: number, infantIndex: number) => (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const age = parseInt(event.target.value, 10);
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, i) => {
        if (i === roomIndex) {
          return {
            ...room,
            infants: room.infants.map((infant, j) => {
              if (j === infantIndex) {
                return { age };
              }

              return infant;
            }),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.infantUpdated, infantIndex + 1, age), Level.POLITE);
  };

  const tracker = getTracker(props.lobState.trackingEnabled, props.analytics, props.lobState.moduleName);
  const _guests = "guests.";
  const lobId = props.lobState.config.elementId || "";
  const isFlight = lobId.includes("flight");

  const increaseAdults = isFlight
    ? formatText(locKeys.increaseAdults)
    : formatText(locKeys.increaseAdultsInRoom, props.index + 1);
  const decreaseAdults = isFlight
    ? formatText(locKeys.decreaseAdults)
    : formatText(locKeys.decreaseAdultsInRoom, props.index + 1);
  const increaseChildren = isFlight
    ? formatText(locKeys.increaseChildren)
    : formatText(locKeys.increaseChildrenInRoom, props.index + 1);
  const decreaseChildren = isFlight
    ? formatText(locKeys.decreaseChildren)
    : formatText(locKeys.decreaseChildrenInRoom, props.index + 1);
  const increaseInfants = isFlight
    ? formatText(locKeys.increaseInfants)
    : formatText(locKeys.increaseInfantInRoom, props.index + 1);
  const decreaseInfants = isFlight
    ? formatText(locKeys.decreaseInfants)
    : formatText(locKeys.decreaseInfantsInRoom, props.index + 1);

  return (
    <div className="roomPickerRoom" data-testid={`room-${props.index + 1}`}>
      {props.config.withRooms && (
        <h3
          className="roomHeader uitk-type-bold no-outline all-t-padding-two"
          aria-label={formatText(locKeys.roomHeaderAriaLabel, props.index + 1, props.travelers.rooms.length)}
          tabIndex={0}
          ref={roomRef}
          data-testid="room-header"
        >
          <span aria-hidden="true">{formatText(locKeys.roomHeaderTitle, props.index + 1)}</span>
        </h3>
      )}
      <UitkStepInput
        title={formatText(locKeys.adults)}
        increaseText={increaseAdults}
        decreaseText={decreaseAdults}
        caption={formatText(props.config?.adultsCaptionToken || "")}
        max={props.config.maxAdultsPerRoom}
        min={props.config.minAdultsPerRoom}
        value={props.room.adults}
        onChange={onUpdateRoomAdults(props.index)}
        className="adultStepInput"
        id={`adult-input-${props.index}`}
      />
      <UitkStepInput
        title={formatText(locKeys.children)}
        increaseText={increaseChildren}
        decreaseText={decreaseChildren}
        caption={formatText(props.config.childrenCaptionToken)}
        max={props.config.maxChildrenPerRoom}
        min={props.config.minChildrenPerRoom}
        value={props.room.children.length}
        onChange={onUpdateRoomChildren(props.index)}
        className="childStepInput"
        id={`child-input-${props.index}`}
      />
      {props.room.children && props.room.children.length > 0 && props.config.withAges && (
        <AgeSelect
          ageOptions={props.childAgeOptions}
          onChange={onUpdateChildAge}
          children={props.room.children}
          padding={{ blockstart: "one", blockend: "three" }}
          menuIndex={props.index}
          labelKey={locKeys.childAgeLabel}
          id="child-age-input"
        />
      )}
      {props.config.withInfants && (
        <>
          <UitkStepInput
            title={formatText(locKeys.infants)}
            increaseText={increaseInfants}
            decreaseText={decreaseInfants}
            caption={formatText(props.config.infantsCaptionToken)}
            max={props.config.maxInfantsPerRoom}
            min={props.config.minInfantsPerRoom}
            value={props.room.infants.length}
            onChange={onUpdateRoomInfants(props.index)}
            className="childStepInput"
            id={`infant-input-${props.index}`}
          />
          {props.config.withAges && (
            <AgeSelect
              ageOptions={props.infantAgeOptions}
              onChange={onUpdateInfantAge}
              children={props.room.infants}
              padding={{ blockstart: "one" }}
              dataTestid="infant-ages"
              menuIndex={props.index}
              labelKey={locKeys.infantAgeLabel}
              id="infant-age-input"
            />
          )}
        </>
      )}
      {props.index > 0 && (
        <div className="removeRoomButton uitk-type-right all-t-padding-four">
          <UitkTertiaryButton onClick={onRemoveRoom(props.index)} text={formatText(locKeys.removeRoom)} />
        </div>
      )}
    </div>
  );
});
